/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { Box, BoxProps } from "@mui/material";
import CheckboxInput from "../CheckboxInput";

export interface FieldsetGroupProps {
    children: React.ReactNode;
    sx?: BoxProps["sx"];
    ref?: BoxProps["ref"];
}

function shouldIgnoreTooltip(children: React.ReactNode): boolean {
    if (!children) return true;
    return React.Children.toArray(children).every(child => {
        if (!React.isValidElement(child)) return true;
        if (child.type === CheckboxInput) return true;
        if (!child.props) return true;
        if (child.props.tooltip) return false;
        return shouldIgnoreTooltip(child.props.children);
    })
}

function ignoreTooltip(children: React.ReactNode): React.ReactNode {
    if (!children) return children;
    return React.Children.map(children, child => {
        if (React.isValidElement(child) && child.props) {
            return React.cloneElement(child, {
                ...child.props,
                ignoreTooltip: child.props.ignoreTooltip ?? true,
                children: ignoreTooltip(child.props.children),
            });
        }

        return child;
    });
}

export default React.forwardRef(function FieldsetGroup(props: FieldsetGroupProps, ref: React.ForwardedRef<unknown>) {
    return <Box sx={props.sx} ref={ref}>
        {shouldIgnoreTooltip(props.children) ? ignoreTooltip(props.children) : props.children}
    </Box>;
});
